import { ComplexValue, CaseData, PrimaryKey, Value, Fields, SubTables } from 'app/types-shared';
import { CaseConnectionParam } from 'app/infrastructure/ui/form/types';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { BowtieDisplayMode } from './external-types';
import { ReactNode } from 'react';

// Bowtie component router props
export interface BowtieExternalProps {
  caseNumber: number;
  connectedDrafts?: CaseConnectionParam[];
  favouriteId?: number;
  footer?: ReactNode;
  displayMode: BowtieDisplayMode;
  instanceKey: string;
}

// Wire Bowtie model
export interface WireBowtieData {
  caseDataModel: WireBowtieCaseModel;
  caseTemplate: CaseRegistrationTemplate;
}

export interface WireBowtieCaseModel {
  anchors: BowtieAnchorRoot[];
  cases: CaseData[];
  otherConnections: WireBowtieCaseOtherConnection[];
  casesETags: Record<number, string>;
}

export interface WireBowtieCaseOtherConnection {
  caseId: number;
  caseType: CodetableWireFormat;
  connectionType: CodetableWireFormat;
  count: number;
}

// Wire Layout model
export interface BowtieLayout {
  layoutTypeAttributes: { [key: string]: string };
  layoutTypeAttributesCaseType: LayoutTypeAttributesCaseType[];
}

export interface LayoutTypeAttributesCaseType {
  caseType: number;
  attributes: { [key: string]: string };
}

// Bowtie diagram model
export interface BowtieAnchorRoot {
  caseId: number;
  category: number;
  connectedCases: BowtieConnectedCases[];
}

export interface BowtieConnectedCases {
  category: number;
  subordinateCases: BowtieSubordinateCases[];
}

export interface BowtieSubordinateCases {
  caseId: number;
  connected?: BowtieConnectedCases[];
}

export interface BowtieCaseOtherConnection {
  caseId: number;
  caseType: ComplexValue<number>;
  connectionType: ComplexValue<number>;
  count: number;
}

export interface BowtieCase {
  primaryKey: PrimaryKey;
  caseNumber: number;
  caseType: Value;
  fields: Fields;
  subTables?: SubTables;
}

export interface LayoutTypeAttributesCaseType {
  caseType: number;
  attributes: { [key: string]: string };
}

export interface BowtieCaseModel {
  anchors: BowtieAnchorRoot[];
  cases: BowtieCase[];
  otherConnections: BowtieCaseOtherConnection[];
  casesETags: Record<number, string>;
}

interface FieldTemplate {
  text: string;
}

export interface CaseRegistrationTemplate {
  caseDataTemplate: {
    fields?: {
      [key: string]: FieldTemplate;
    };
  };
  labels: { [key: string]: FieldTemplate };
}

export interface DraftConnections {
  caseNumber: number;
  connections: ComplexValue<number>[];
  updating: boolean;
}

export type BowtieAppCaseModel = BowtieCaseModel & BowtieLayout;

export interface BowtieAppDataModel {
  caseDataModel: BowtieAppCaseModel;
  caseTemplate: CaseRegistrationTemplate;
  draftConnections: DraftConnections[];
  displayMode: BowtieDisplayMode;
}

export interface BowtieApp {
  draw: (dataModel: BowtieAppDataModel, resetZoom: boolean, connectMode: boolean) => void;
  dispose: () => void;
}

export enum BowtieTooltipType {
  caseData,
  connections,
  meatBallCaseConnectDropdown,
  caseConnectDropdown
}

export interface Codetable {
  v: number;
  d: string;
  rootCodes: CodetableWireFormat[];
}

export interface BowtieTooltipOptions {
  tooltipTemplate: string;
  showEvaluations: boolean;
  showConnections: boolean;
  showChangeSequenceOptions: boolean;
}

export interface WireChecklistEvaluationItem {
  checkListItem: CodetableWireFormat;
  evaluations: {
    evaluation: CodetableWireFormat;
    count: number;
  }[];
}

export interface WireChecklistEvaluationData {
  caseId: number;
  checkListItems: WireChecklistEvaluationItem[];
}
